/**
 * TOSS Web Application — Client portal: one table the portal's chrome is derived from
 *
 * Twenty routes, and until now five separate places decided what each of them looks like: three
 * hand-written arrays (`FULL_SCREEN_PHONE_ROUTES`, `OWN_APP_BAR_PHONE_ROUTES`, `CREATE_FAB_ROUTES`),
 * two hand-written segment parsers (`isClientApplicationCardPath`, `isClientChatPath`, each with a
 * paragraph explaining why a prefix match could not express «exactly one segment more»), a
 * `startsWith` for the viewport lock, and a fourth list for the back arrow's parent. Adding a
 * screen meant remembering all of them, and forgetting one is silent: nothing throws, a screen
 * simply grows a second header or loses its only way back. That is exactly how «Сотрудники» and
 * «Роли и права» shipped with two stacked bars and no exit at all.
 *
 * Here every one of those answers is a column, and the matching is done by `matchRoutes` — the
 * router's own ranking matcher, the same code that resolves the route table itself. Which removes
 * the segment parsers outright: `/client/applications`, `/client/applications/new` and
 * `/client/applications/:id` are three patterns, and a static segment beats a dynamic one by
 * construction, so `new` can never be read as an id.
 *
 * **Order in this array does not matter.** That is the point of using the router's matcher rather
 * than a first-match loop, and `clientRouteRegistry.test.ts` asserts it by shuffling.
 *
 * The three anonymous screens (`/client/login` and friends) are deliberately absent: they render
 * outside `ClientLayout` and have no chrome of the portal's to decide.
 */

import { matchRoutes } from 'react-router-dom';
import { ROUTES } from '@/constants';

// ============================================================================
// Types
// ============================================================================

/**
 * What KIND of screen this is — the product question, independent of width.
 *
 *  - `section` — a place the customer lives in, and one the menu names.
 *  - `screen` — a detail of something: a card, a sub-page, a read-only list opened on purpose.
 *  - `task` — a form with a goal, and something to lose on the way out.
 */
export type ClientScreenKind = 'section' | 'screen' | 'task';

/**
 * Which bar the PHONE draws, and who draws it.
 *
 *  - `section` — the page brings its own `ClientSectionAppBar`; the bottom bar stays.
 *  - `screen` — the page brings `ClientScreenAppBar` and takes the whole viewport: no bottom bar,
 *    and the back arrow (plus the device's control and an edge swipe) is the only way off it. A
 *    floating button is still allowed here and «Шаблоны» has one (28.08.2026, owner) — it simply
 *    sits 64 px lower, because there is no bar under it to clear; `ClientFab` reads that off this
 *    very field.
 *
 * There is no third value. `ClientLayout` still has a branch that draws the shared `ClientHeader`
 * on a phone, and it is unreachable — every route answers one of these two. The branch is kept
 * because the layout is shared code, not because a route needs it.
 */
export type ClientPhoneChrome = 'section' | 'screen';

/**
 * Кто рисует плавающую кнопку в правом нижнем углу телефона — и рисует ли вообще.
 *
 *  - `'application'` — её рисует слой: это «новая заявка», один и тот же адрес на всех
 *    экранах, где создать заявку — правдоподобный следующий шаг.
 *  - `'page'` — слой только РЕЗЕРВИРУЕТ под неё место в конце прокрутки, а саму кнопку
 *    монтирует экран. Так сделано для «Сотрудников»: их кнопка открывает форму, которая живёт
 *    в адресе через `useClientOverlaySubject`, а тот хук требует объект-субъект — ссылка на
 *    `?sheet=employee-form` без него закрылась бы сама на первом же эффекте.
 *
 * Отсутствие поля — кнопки нет и места под неё не резервируется.
 */
export type ClientFabOwner = 'application' | 'page';

export interface ClientRouteNode {
  /** Exactly the pattern from `ROUTES.client.*`, so renaming a path breaks the build. */
  path: string;
  /**
   * The screen this one sits INSIDE — where the back arrow goes when there is no history to
   * return to. `null` for the five addresses of the bottom bar, which contain nothing above them
   * and therefore draw no arrow.
   *
   * Not «where back leads»: that is the previous entry whenever there is one. See `useClientBack`.
   */
  parent: string | null;
  kind: ClientScreenKind;
  phoneChrome: ClientPhoneChrome;
  /** Sizes itself against the viewport instead of growing with its content — the chat. */
  viewportLocked?: boolean;
  /** Кто рисует плавающую кнопку телефона — см. {@link ClientFabOwner}. */
  fab?: ClientFabOwner;
  /** Exists on a phone only; at ≥768 the same content is a dialog on the parent screen. */
  phoneOnly?: boolean;
  /**
   * Красит канву `<main>` на телефоне в `--client-canvas` — серое поле, на котором белая карточка
   * читается как карточка, а не как дефект отрисовки. Это вид мобильного приложения TOSS
   * («Прочее» и всё, что за ним), и берут его только помеченные экраны: остальной портал живёт на
   * `--color-bg-app`. Без пометки — прежнее поведение дословно.
   */
  phoneCanvas?: 'grouped';
  /**
   * Чем система красит полосу над вебвью — статус-бар установленного приложения и адресную строку
   * браузера телефона.
   *
   * Правило владельца (01.09.2026): полоса продолжает то, что под ней. У «Главной», «Заявок»,
   * «Документов» и списка переписок под ней белая панель `ClientSectionAppBar` — значит белая
   * (`'card'`). У профиля, «Ролей», самой переписки и всех экранов `ClientScreenAppBar` под ней
   * канва — значит канва, и это умолчание.
   *
   * Признак живёт здесь, а не в самой шапке, по той же причине, что и `phoneCanvas`: `<meta
   * name="theme-color">` в документе один, ставить его должен один хозяин, и хозяин этот —
   * `ClientLayout`, который знает адрес. Шапка монтируется и размонтируется страницей, и две
   * шапки в переходе между экранами подрались бы за один тег.
   */
  phoneStatusBar?: 'card';
  /** Leaving with unsaved input asks first. */
  guarded?: boolean;
}

// ============================================================================
// The table
// ============================================================================

export const CLIENT_ROUTE_TREE: readonly ClientRouteNode[] = [
  // ── Работа: the four addresses of the bottom bar the customer lives on ──────────────────────
  {
    path: ROUTES.client.dashboard,
    parent: null,
    kind: 'section',
    phoneChrome: 'section',
    fab: 'application',
    phoneCanvas: 'grouped',
    // Под полосой белая панель бара — см. `phoneStatusBar`.
    phoneStatusBar: 'card',
  },
  {
    path: ROUTES.client.applications.list,
    parent: null,
    kind: 'section',
    phoneChrome: 'section',
    fab: 'application',
    phoneCanvas: 'grouped',
    // Под полосой белая панель бара — см. `phoneStatusBar`.
    phoneStatusBar: 'card',
  },
  {
    path: ROUTES.client.documents,
    parent: null,
    kind: 'section',
    phoneChrome: 'section',
    phoneCanvas: 'grouped',
    // Под полосой белая панель бара — см. `phoneStatusBar`.
    phoneStatusBar: 'card',
  },
  {
    // The list scrolls inside itself, which is what makes two columns possible on a computer.
    //
    // `fab: 'page'` — кнопку «Новый чат» монтирует страница, а не слой: её видно только тем, кому
    // сервер разрешил создавать переписки (`canCreateChats`), и только на списке — в самой
    // переписке нижний край принадлежит полю ввода. Места в конце прокрутки маршрут при этом не
    // резервирует: он viewport-locked, прокрутка внутри списка, и место под кнопку список
    // отмеряет сам.
    path: ROUTES.client.messenger,
    parent: null,
    kind: 'section',
    phoneChrome: 'section',
    viewportLocked: true,
    fab: 'page',
    phoneCanvas: 'grouped',
    // Под полосой белая панель бара — см. `phoneStatusBar`.
    phoneStatusBar: 'card',
  },

  // ── Заявки ─────────────────────────────────────────────────────────────────────────────────
  {
    // A TASK, not a section: a bottom bar under a form is five ways to lose what has been typed.
    path: ROUTES.client.applications.create,
    parent: ROUTES.client.applications.list,
    kind: 'task',
    phoneChrome: 'screen',
    guarded: true,
    phoneCanvas: 'grouped',
  },
  {
    // Правка непринятой заявки (31.08.2026) — ТА ЖЕ форма, что и создание, и та же причина
    // держать её задачей: нижняя панель под формой это пять способов потерять набранное.
    // Записи здесь не было с самого появления маршрута, и `clientRouteRegistry.test.ts`
    // краснел на «covers every address in ROUTES.client».
    path: ROUTES.client.applications.edit,
    parent: ROUTES.client.applications.list,
    kind: 'task',
    phoneChrome: 'screen',
    guarded: true,
    phoneCanvas: 'grouped',
  },
  {
    // Full screen since the card lost its tabs: one flow, and the bottom bar's five items all
    // lead away from what was just opened.
    path: ROUTES.client.applications.details,
    parent: ROUTES.client.applications.list,
    kind: 'screen',
    phoneChrome: 'screen',
    phoneCanvas: 'grouped',
  },

  // ── Сообщения ──────────────────────────────────────────────────────────────────────────────
  {
    // The conversation draws `ClientChatHeader` — the same role a section bar plays: the page
    // brings the bar, the layout draws none, the bottom bar stays.
    path: ROUTES.client.messengerChat,
    parent: ROUTES.client.messenger,
    kind: 'screen',
    phoneChrome: 'section',
    viewportLocked: true,
    phoneCanvas: 'grouped',
  },

  // ── Справочники: reached from the profile hub on a phone, from the sidebar on a computer ────
  {
    path: ROUTES.client.templates,
    parent: ROUTES.client.profile,
    kind: 'section',
    phoneChrome: 'screen',
    phoneCanvas: 'grouped',
    // Кнопку рисует экран: «Создать шаблон» ОТСУТСТВУЕТ на потолке в пятьдесят заготовок, а знает
    // об этом только список — он здесь загружен целиком. Слой такого условия не видит.
    fab: 'page',
  },
  // Форма заготовки — на ОБЕИХ ширинах (27.08.2026). `phoneOnly` снят вместе с диалогом на
  // компьютере: заготовку правит форма ЗАЯВКИ в режиме шаблона, а она везде страница. Флаг был бы
  // не косметикой, а поломкой — `ClientLayout` уводил бы «Изменить поля» обратно в список, и на
  // компьютере пункт кебаба не делал бы ничего.
  {
    path: ROUTES.client.templateNew,
    parent: ROUTES.client.templates,
    kind: 'task',
    phoneChrome: 'screen',
    guarded: true,
    phoneCanvas: 'grouped',
  },
  {
    path: ROUTES.client.templateEdit,
    parent: ROUTES.client.templates,
    kind: 'task',
    phoneChrome: 'screen',
    guarded: true,
    phoneCanvas: 'grouped',
  },
  {
    path: ROUTES.client.containers.list,
    parent: ROUTES.client.profile,
    kind: 'section',
    phoneChrome: 'screen',
    phoneCanvas: 'grouped',
  },
  {
    path: ROUTES.client.containers.details,
    parent: ROUTES.client.containers.list,
    kind: 'screen',
    phoneChrome: 'screen',
    phoneCanvas: 'grouped',
  },
  {
    path: ROUTES.client.ptos,
    parent: ROUTES.client.profile,
    kind: 'section',
    phoneChrome: 'screen',
    phoneCanvas: 'grouped',
  },

  // ── Компания ───────────────────────────────────────────────────────────────────────────────
  {
    // `section`, not `screen`, and that is why it keeps the bottom bar: its search and «+» are
    // pinned INSIDE its own bar, which `ClientScreenAppBar`'s single action slot cannot hold.
    path: ROUTES.client.employees,
    parent: ROUTES.client.profile,
    kind: 'section',
    phoneChrome: 'section',
    phoneCanvas: 'grouped',
    // Кнопку рисует экран: она открывает форму сотрудника, а та живёт в адресе объектом.
    fab: 'page',
  },
  {
    // The permission matrix scrolls sideways, which is also why the edge swipe stays off here —
    // see `ClientLayout`.
    path: ROUTES.client.employeeRoles,
    parent: ROUTES.client.employees,
    kind: 'screen',
    phoneChrome: 'section',
    phoneCanvas: 'grouped',
  },

  // ── Личное ─────────────────────────────────────────────────────────────────────────────────
  {
    path: ROUTES.client.profile,
    parent: null,
    kind: 'section',
    phoneChrome: 'section',
    phoneCanvas: 'grouped',
  },
  {
    path: ROUTES.client.profilePersonal,
    parent: ROUTES.client.profile,
    kind: 'screen',
    phoneChrome: 'screen',
    phoneOnly: true,
    phoneCanvas: 'grouped',
    // С 28.08.2026 экран правит профиль на месте, а «Отмены» на плашке больше нет: уйти с
    // набранным спрашивает `useClientLeaveGuard` — тот же единственный на роутер блокировщик,
    // из-за которого две помеченные формы в одной цепочке запрещены (см. тест реестра).
    guarded: true,
  },
  {
    path: ROUTES.client.profileCompany,
    parent: ROUTES.client.profile,
    kind: 'screen',
    phoneChrome: 'screen',
    phoneOnly: true,
    phoneCanvas: 'grouped',
  },
  {
    path: ROUTES.client.profileColleagues,
    parent: ROUTES.client.profile,
    kind: 'screen',
    phoneChrome: 'screen',
    phoneOnly: true,
    phoneCanvas: 'grouped',
  },
  {
    path: ROUTES.client.profilePassword,
    parent: ROUTES.client.profile,
    kind: 'task',
    phoneChrome: 'screen',
    phoneOnly: true,
    phoneCanvas: 'grouped',
  },
  {
    // A feed opened from the bell, read, and left. The bell is not a screen, so the screen under
    // it is the dashboard.
    path: ROUTES.client.notifications,
    parent: ROUTES.client.dashboard,
    kind: 'section',
    phoneChrome: 'screen',
    phoneCanvas: 'grouped',
  },
];

// ============================================================================
// Matching
// ============================================================================

/** `matchRoutes` wants route objects; the id carries the pattern back to its node. */
const MATCHABLE = CLIENT_ROUTE_TREE.map((node) => ({ path: node.path, id: node.path }));

const BY_PATH = new Map(CLIENT_ROUTE_TREE.map((node) => [node.path, node]));

/**
 * The node for an address, or `null` for one the portal does not own.
 *
 * The trailing slash is normalised first: `useLocation().pathname` keeps one and the matcher does
 * not, and without this `/client/profile/personal/` would read as an unknown screen with no way
 * back.
 */
export function matchClientRoute(pathname: string): ClientRouteNode | null {
  const normalized =
    pathname.length > 1 && pathname.endsWith('/') ? pathname.slice(0, -1) : pathname;

  const matches = matchRoutes(MATCHABLE, normalized);
  if (!matches || matches.length === 0) return null;

  const id = matches[matches.length - 1].route.id;

  return (id && BY_PATH.get(id)) ?? null;
}

/** The node for a pattern, for tests and for anything working from `ROUTES.client.*` directly. */
export function clientRouteNode(path: string): ClientRouteNode | undefined {
  return BY_PATH.get(path);
}

// ============================================================================
// Derived answers — the whole point of the table
// ============================================================================

/** The screen this one sits inside, or `null` for a root and for an unknown address. */
export function clientRouteParent(pathname: string): string | null {
  return matchClientRoute(pathname)?.parent ?? null;
}

/** True for a route that takes the whole phone viewport. Width is the caller's business. */
export function isFullScreenPhonePath(pathname: string): boolean {
  return matchClientRoute(pathname)?.phoneChrome === 'screen';
}

/** True when the page draws its own phone app bar and the layout draws none. */
export function isOwnAppBarPhonePath(pathname: string): boolean {
  return matchClientRoute(pathname)?.phoneChrome === 'section';
}

/**
 * Кто рисует плавающую кнопку на этом маршруте, или `null`, если её здесь нет.
 *
 * Оба непустых значения одинаково означают «место под кнопку в конце прокрутки зарезервировать»;
 * различаются они только тем, монтирует ли слой саму кнопку. См. {@link ClientFabOwner}.
 */
export function clientFabOwner(pathname: string): ClientFabOwner | null {
  return matchClientRoute(pathname)?.fab ?? null;
}

/** True for a page that sizes itself against the viewport instead of growing with its content. */
export function isViewportLockedPath(pathname: string): boolean {
  return matchClientRoute(pathname)?.viewportLocked === true;
}

/** True for a screen that exists on a phone only and redirects to its parent at ≥768. */
export function isPhoneOnlyPath(pathname: string): boolean {
  return matchClientRoute(pathname)?.phoneOnly === true;
}

/**
 * True для экрана, чья канва на телефоне — серое поле мобильного приложения, а не общая канва
 * портала. Ширину решает вызывающий: на компьютере эти же адреса рисуют сетку карточек, и серого
 * поля под ней быть не должно.
 */
export function usesGroupedPhoneCanvas(pathname: string): boolean {
  return matchClientRoute(pathname)?.phoneCanvas === 'grouped';
}

/**
 * Чем красится системная полоса над вебвью на этом маршруте: `'card'` — поверхность (белая панель
 * бара), `'canvas'` — канва экрана.
 *
 * Возвращает имя ТОКЕНА, а не цвет: при выбранном пресете фона обе поверхности пересчитываются
 * через `color-mix()` (`:root[data-app-bg="custom"]` в `index.css`), и литерал разошёлся бы с
 * экраном ровно так, как `--client-canvas` расходился с канвой до 27.08.2026.
 */
export function clientPhoneStatusBarSurface(pathname: string): 'card' | 'canvas' {
  return matchClientRoute(pathname)?.phoneStatusBar ?? 'canvas';
}
